/**
 * Overlay user inline-fixes onto built records. Edits are keyed
 * `${recordIndex}:${instanceIndex}:${fieldKey}` and store the RAW string the
 * user typed; the field's transform is re-applied so a fixed cell is typed +
 * re-validated exactly like the original import. Pure + unit-tested.
 */
import { applyTransform } from './transforms'
import type { BuiltRecord } from './build-records'
import type { ColumnTransform } from './mapping-types'

export function editKey(recordIndex: number, instanceIndex: number, fieldKey: string): string {
  return `${recordIndex}:${instanceIndex}:${fieldKey}`
}

export function mergeEdits(
  records: BuiltRecord[],
  edits: Record<string, string>,
  getTransform: (fieldKey: string) => ColumnTransform,
): BuiltRecord[] {
  if (Object.keys(edits).length === 0) return records
  return records.map((rec, ri) => ({
    ...rec,
    instances: rec.instances.map((inst, ii) => {
      const prefix = `${ri}:${ii}:`
      const relevant = Object.keys(edits).filter((k) => k.startsWith(prefix))
      if (relevant.length === 0) return inst
      const fieldValues = { ...inst.fieldValues }
      const cellErrors = { ...inst.cellErrors }
      for (const k of relevant) {
        const fieldKey = k.slice(prefix.length)
        const { value, error } = applyTransform(edits[k], getTransform(fieldKey))
        if (value === undefined) delete fieldValues[fieldKey]
        else fieldValues[fieldKey] = value
        if (error) cellErrors[fieldKey] = error
        else delete cellErrors[fieldKey]
      }
      return { ...inst, fieldValues, cellErrors }
    }),
  }))
}
